import React from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAttemptHistory } from '../hooks/useAttemptHistory';
import { AttemptHistoryTable } from '../components/AttemptHistoryTable';
import { RetryAttemptButton } from '../components/RetryAttemptButton';
import { useTranslation } from '../../../i18n';

export const AttemptHistoryPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const simulationId = searchParams.get('simulationId') || '';
  const { simulation, attempts, quota, loading, error, refresh } =
    useAttemptHistory(simulationId);

  const translateCategory = (cat: string): string => {
    const catMap: Record<string, string> = {
      'customer service': 'category.customer_service',
      'customer_service': 'category.customer_service',
      'sales': 'category.sales',
      'compliance': 'category.compliance',
      'de_escalation': 'category.de_escalation',
      'de-escalation': 'category.de_escalation',
      'coaching': 'category.coaching',
      'general scenario': 'category.general_scenario',
      'general_scenario': 'category.general_scenario',
      'scenario': 'category.general_scenario',
    };
    const key = catMap[cat.toLowerCase().trim()];
    return key ? t(key, undefined, cat) : cat;
  };

  const translateDifficulty = (diff: string): string => {
    const diffMap: Record<string, string> = {
      beginner: 'difficulty.beginner',
      intermediate: 'difficulty.intermediate',
      advanced: 'difficulty.advanced',
      standard: 'difficulty.standard',
    };
    const key = diffMap[diff.toLowerCase().trim()];
    return key ? t(key, undefined, diff) : diff;
  };

  const rawCategory = simulation?.category || 'Scenario';
  const rawDifficulty = simulation?.difficulty || 'Standard';

  return (
    <div className="admin-page">
      <div className="admin-breadcrumbs">
        <Link to="/dashboard">
          <svg style={{ width: '13px', height: '13px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          {t('breadcrumb.dashboard', undefined, 'Dashboard')}
        </Link>
        <span className="breadcrumb-separator">›</span>
        <Link to="/attempts">{t('breadcrumb.my_attempts', undefined, 'My Attempts')}</Link>
        <span className="breadcrumb-separator">›</span>
        <span className="current">{t('breadcrumb.attempt_history', undefined, 'Attempt History')}</span>
      </div>

      {/* Header Info */}
      <div style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', background: 'var(--blue-soft)', color: 'var(--blue)', padding: '3px 8px', borderRadius: '5px' }}>
                {translateCategory(rawCategory)}
              </span>
              <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', background: 'var(--surface)', color: 'var(--muted)', padding: '3px 8px', borderRadius: '5px', border: '1px solid var(--line)' }}>
                {translateDifficulty(rawDifficulty)}
              </span>
            </div>
            <h1 style={{ fontFamily: 'var(--display)', fontSize: '24px', fontWeight: 600, color: 'var(--navy)', margin: 0 }}>
              {simulation?.title || t('attempts.history_title_default', undefined, 'Scenario Attempt History')}
            </h1>
            <p style={{ fontSize: '13.5px', color: 'var(--muted)', margin: '4px 0 0' }}>
              {t('attempts.history_subtitle', undefined, 'Showing all recorded attempts and progression for this scenario.')}
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {quota && (
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--faint)', display: 'block' }}>
                  {t('attempts.meta.quota', undefined, 'Quota')}
                </span>
                <span style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--navy)' }}>
                  {t('attempts.history_quota_used', { used: quota.attemptsUsed, max: quota.maxAttempts, remaining: quota.attemptsRemaining }, `${quota.attemptsUsed} / ${quota.maxAttempts} used (${quota.attemptsRemaining} left)`)}
                </span>
              </div>
            )}

            {simulationId && (
              <RetryAttemptButton
                scenarioId={simulationId}
                quota={quota}
                size="md"
              />
            )}
          </div>
        </div>
      </div>

      {/* Error / Table View */}
      {error ? (
        <div className="admin-alert admin-alert--error" style={{ textAlign: 'center', padding: '24px' }}>
          <p style={{ margin: '0 0 12px', fontWeight: 600 }}>{error}</p>
          <button
            type="button"
            onClick={refresh}
            className="admin-btn admin-btn--primary admin-btn--sm"
          >
            {t('common.retry', undefined, 'Retry')}
          </button>
        </div>
      ) : (
        <AttemptHistoryTable
          attempts={attempts}
          scenarioId={simulationId}
          loading={loading}
        />
      )}
    </div>
  );
};
